import React, { useEffect, useState } from 'react';
import FormCard from '../../../../../components/FormCard';
import Button from '../../../../../components/Button';
import styles from '../../../css/inventoryItemDetail.module.css';
import rsStyles from '../../../Recieve Stock/css/receiveStock.module.css';
import useDispensaryForm from '../logic/useDispensaryForm.jsx';
import useExpiryEditor from '../logic/useExpiryEditor.jsx';
import ExpiryModal from '../presentational/ExpiryModal';
import CenteredBar from '../presentational/CenteredBar';
import RowTop from '../presentational/dispensary/RowTop';
import RowMid from '../presentational/dispensary/RowMid';
import RowQuantity from '../presentational/dispensary/RowQuantity';
import RowCategory from '../presentational/dispensary/RowCategory';
import RowBottom from '../presentational/dispensary/RowBottom';
import { fetchStrengthOptions, fetchDosageFormOptions } from '../../../../../api/Inventory/RecieveStockApi.js';

export default function DispensaryFormImpl({
  showUpdate = true,
  otherCategorySelected,
  onOtherCategoryChange,
  onSubmit,
  categories = [],
  errors = {},
  departmentName,
  scanDrug,
}) {
  const f = useDispensaryForm({ onOtherCategoryChange });
  const exp = useExpiryEditor();

  const [strengthOptions, setStrengthOptions] = useState([]);
  const [dosageFormOptions, setDosageFormOptions] = useState([]);
  const [otherStrength, setOtherStrength] = useState('');
  const [otherDosageForm, setOtherDosageForm] = useState('');

  const payload = {
    type: 'drug',
    name: f.drugName,
    barcode: f.barcode,
    activeingredient: f.activeIngredient,
    packsize: String(f.packSize),
    strength: f.strength,
    nappicode: f.nappiCode,
    schedule: Number(f.schedule),
    binnumber: f.binNumber,
    expirydates: exp.expiryList.filter(Boolean),
    dosageform: f.dosageForm,
    reorderlevel: Number(f.reorderLevel),
    quantity: Number(f.quantity),
    costprice: f.parsedCost,
    note: f.note,
    category: f.rawCategory === 'Other' ? f.customCategory : f.rawCategory,
    otherStrengthName: otherStrength,
    otherDosageFormName: otherDosageForm,
  };

  const isFormComplete = () => {
    const hasName = !!f.drugName;
    const hasBarcode = !!f.barcode;
    const hasStrength = !!f.strength && (f.strength !== 'Other' || !!otherStrength);
    const hasPackSize = !!f.packSize;
    const hasDosageForm = !!f.dosageForm && (f.dosageForm !== 'Other' || !!otherDosageForm);
    const hasSchedule = f.schedule !== '' && f.schedule !== null && f.schedule !== undefined;
    const hasQuantity = Number(f.quantity) > 0;
    const hasReorder = Number(f.reorderLevel) >= 0;
    const hasCost = Number(f.parsedCost) > 0;
    const hasCategory = f.rawCategory === 'Other' ? !!f.customCategory : !!f.rawCategory;
    const hasExpiry = Array.isArray(exp.expiryList) && exp.expiryList.filter(Boolean).length > 0;

    return (
      hasName &&
      hasBarcode &&
      hasStrength &&
      hasPackSize &&
      hasDosageForm &&
      hasSchedule &&
      hasQuantity &&
      hasReorder &&
      hasCost &&
      hasCategory &&
      hasExpiry
    );
  };

  useEffect(() => {
    if (!scanDrug) return;
    const med = scanDrug;
    if (med.name) f.setDrugName(med.name);
    if (med.barcode) f.setBarcode(med.barcode);
    if (med.activeIngredient) f.setActiveIngredient(med.activeIngredient);
    if (med.strength) f.setStrength(med.strength);
    if (med.nappiCode) f.setNappiCode(med.nappiCode);
    if (med.dosageForm) f.setDosageForm(med.dosageForm);
    if (med.schedule != null) f.setSchedule(String(med.schedule));
    if (med.description) f.setNote(med.description);
    if (med.reorderLevel != null) f.setReorderLevel(med.reorderLevel);
    if (med.costPrice != null) f.setCost(String(med.costPrice));
    if (med.categoryName) {
      f.setCategory(med.categoryName);
    }
  }, [scanDrug]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [strengths, dosageForms] = await Promise.all([
          fetchStrengthOptions(),
          fetchDosageFormOptions(),
        ]);
        if (!mounted) return;
        if (Array.isArray(strengths)) setStrengthOptions(strengths);
        if (Array.isArray(dosageForms)) setDosageFormOptions(dosageForms);
      } catch (err) {
        console.error('Failed to load strength/dosage form options', err);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <>
      <FormCard compact={otherCategorySelected}>
        <RowTop
          styles={styles}
          rsStyles={rsStyles}
          f={f}
          exp={exp}
          errors={errors}
          departmentName={departmentName}
          strengthOptions={strengthOptions}
          otherStrength={otherStrength}
          setOtherStrength={setOtherStrength}
        />
      </FormCard>

      <FormCard compact={otherCategorySelected}>
        <RowMid styles={styles} rsStyles={rsStyles} f={f} errors={errors} />
      </FormCard>

      <FormCard compact={otherCategorySelected}>
        <RowQuantity
          styles={styles}
          rsStyles={rsStyles}
          f={f}
          errors={errors}
          dosageFormOptions={dosageFormOptions}
          otherDosageForm={otherDosageForm}
          setOtherDosageForm={setOtherDosageForm}
        />
      </FormCard>

      <FormCard compact={otherCategorySelected}>
        <RowCategory styles={styles} rsStyles={rsStyles} categories={categories} f={f} exp={exp} errors={errors} />
      </FormCard>

      <FormCard compact={otherCategorySelected}>
        <RowBottom styles={styles} rsStyles={rsStyles} categories={categories} f={f} errors={errors} />
      </FormCard>

      {exp.showExpiryModal && (
        <ExpiryModal styles={styles} list={exp.expiryList} today={exp.todayStr} setCount={exp.setCount} onChange={exp.updateExpiryAt} onRemove={exp.removeExpiryAt} onClose={() => exp.setShowExpiryModal(false)} />
      )}

      {showUpdate && (
        <CenteredBar>
          <Button
            disabled={!isFormComplete()}
            onClick={() => {
              if (!isFormComplete()) return;
              return Promise.resolve(onSubmit?.(payload)).then(async success => {
                if (success) {
                  try {
                    const [strengths, dosageForms] = await Promise.all([
                      fetchStrengthOptions(),
                      fetchDosageFormOptions(),
                    ]);
                    if (Array.isArray(strengths)) setStrengthOptions(strengths);
                    if (Array.isArray(dosageForms)) setDosageFormOptions(dosageForms);
                  } catch (err) {
                    console.error('Failed to refresh strength/dosage form options after save', err);
                  }

                  f.resetForm();
                  setOtherStrength('');
                  setOtherDosageForm('');
                }
              });
            }}
          >
            Save
          </Button>
        </CenteredBar>
      )}
    </>
  );
}
